import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { useSessionStore } from '@/stores/components/sessionStore'
import { useAssinaturaStore } from '@/stores/components/assinaturaStore'
import router from '@/router'
import { push } from 'notivue'

const URL = import.meta.env.VITE_URL_API as string
const URLAplication = import.meta.env.VITE_URL_APPLICATION as string

type ApiRequestConfig = AxiosRequestConfig & {
  skipUnauthorizedRedirect?: boolean
}

// Cria instância tipada
const apiInstance: AxiosInstance = axios.create({
  baseURL: URL,
  timeout: 30000,
})

apiInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const sessionStore = useSessionStore()

    const token = sessionStore.token
    const empresaUuid = sessionStore.currentEmpresa?.uuid_emp_id

    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`)
    }

    if (empresaUuid) {
      config.headers.set('empresalogada', empresaUuid)
    }

    return config
  },
  (error) => Promise.reject(error)
)

function handleSubscriptionBlockedResponse(error: any) {
  if (
    error.response?.status === 402 &&
    error.response?.data?.error?.code === 'SUBSCRIPTION_BLOCKED'
  ) {
    const assinaturaStore = useAssinaturaStore()
    assinaturaStore.openBlockedModal(error.response.data.error.detail)
  }
}

const responseInterceptor = (response: AxiosResponse) => response
const responseErrorInterceptor = (error: any) => {
  const sessionStore = useSessionStore()
  const requestConfig = error.config as ApiRequestConfig | undefined

  if (
    !requestConfig?.skipUnauthorizedRedirect &&
    (error.response?.status === 401 ||
      error.response?.data?.error?.code === 'UNAUTHORIZED_ACCESS')
  ) {
    if (window.location.pathname !== '/login') {
      sessionStore.clearSession()
      push.error('Sua sessão expirou. Faça o login novamente.')
      router.push('/login')
    }
  }

  handleSubscriptionBlockedResponse(error)

  if (error.response?.status === 409) {
    console.warn('Conflito detectado (409):', error)
  }

  return Promise.reject(error)
}

apiInstance.interceptors.response.use(responseInterceptor, responseErrorInterceptor)

// Cobre chamadas que usam a instância global do axios diretamente (fora do wrapper `Api`),
// já que `import axios from 'axios'` resolve para o mesmo singleton em toda a aplicação.
// Só reage a respostas da nossa própria API: várias telas usam `axios` bruto para
// chamar serviços externos (ex.: IBGE) e não devem ser afetadas por este interceptor.
axios.interceptors.response.use(responseInterceptor, (error) => {
  const requestUrl = [error.config?.baseURL, error.config?.url].filter(Boolean).join('')

  if (!requestUrl.startsWith(URL)) {
    return Promise.reject(error)
  }

  return responseErrorInterceptor(error)
})

const Api = {

  /**
   * Chamadas para a versão 2 da API. Use ex: `Api.v2.get(...)` para chamadas à API v2.
   */
  v2: {
    /**
     * Faz uma requisição GET para a API v2.
     * @param path O caminho da rota (ex: '/users').
     * @param config Configurações adicionais da requisição.
     * @returns Uma Promise com a resposta da requisição.
     */
    get<T = unknown>(path: string, config: ApiRequestConfig = {}) {
      return apiInstance.get<T>(`v2/${path.replace(/^\/+/, '')}`, config)
    },
    /**
     * Faz uma requisição POST para a API v2.
     * @param path O caminho da rota (ex: '/users').
     * @param body O corpo da requisição.
     * @param config Configurações adicionais da requisição.
     * @returns Uma Promise com a resposta da requisição.
     */
    post<T = unknown>(
      path: string,
      body?: unknown,
      config: ApiRequestConfig = {}
    ) {
      return apiInstance.post<T>('v2'+path, body, config)
    },
    /**
     * Faz uma requisição PUT para a API v2.
     * @param path O caminho da rota (ex: '/users').
     * @param body O corpo da requisição.
     * @param config Configurações adicionais da requisição.
     * @returns Uma Promise com a resposta da requisição.
     */
    put<T = unknown>(
      path: string,
      body?: unknown,
      config: ApiRequestConfig = {}
    ) {
      return apiInstance.put<T>('v2'+path, body, config)
    },
    /**
     * Faz uma requisição PATCH para a API v2.
     * @param path O caminho da rota (ex: '/users').
     * @param body O corpo da requisição.
     * @param config Configurações adicionais da requisição.
     * @returns Uma Promise com a resposta da requisição.
     */
    patch<T = unknown>(
      path: string,
      body?: unknown,
      config: ApiRequestConfig = {}
    ) {
      return apiInstance.patch<T>('v2'+path, body, config)
    },
    /**
     * Faz uma requisição DELETE para a API v2.
     * @param path O caminho da rota (ex: '/users').
     * @param config Configurações adicionais da requisição.
     * @returns Uma Promise com a resposta da requisição.
     */
    delete<T = unknown>(path: string, config: ApiRequestConfig = {}) {
      return apiInstance.delete<T>('v2'+path, config)
    },
  },

  /**
   * Faz uma requisição GET para a API.
   * @param path O caminho da rota (ex: '/users').
   * @param config Configurações adicionais da requisição.
   * @returns Uma Promise com a resposta da requisição.
   */
  get<T = unknown>(path: string, config: ApiRequestConfig = {}) {
    return apiInstance.get<T>(path, config)
  },
  /**
   * Faz uma requisição POST para a API.
   * @param path O caminho da rota (ex: '/users').
   * @param body O corpo da requisição.
   * @param config Configurações adicionais da requisição.
   * @returns Uma Promise com a resposta da requisição.
   */
  post<T = unknown>(
    path: string,
    body?: unknown,
    config: ApiRequestConfig = {}
  ) {
    return apiInstance.post<T>(path, body, config)
  },
  /**
   * Faz uma requisição PUT para a API.
   * @param path O caminho da rota (ex: '/users').
   * @param body O corpo da requisição.
   * @param config Configurações adicionais da requisição.
   * @returns Uma Promise com a resposta da requisição.
   */
  put<T = unknown>(
    path: string,
    body?: unknown,
    config: ApiRequestConfig = {}
  ) {
    return apiInstance.put<T>(path, body, config)
  },
  /**
   * Faz uma requisição PATCH para a API.
   * @param path O caminho da rota (ex: '/users').
   * @param body O corpo da requisição.
   * @param config Configurações adicionais da requisição.
   * @returns Uma Promise com a resposta da requisição.
   */
  patch<T = unknown>(
    path: string,
    body?: unknown,
    config: ApiRequestConfig = {}
  ) {
    return apiInstance.patch<T>(path, body, config)
  },
  /**
   * Faz uma requisição DELETE para a API.
   * @param path O caminho da rota (ex: '/users').
   * @param config Configurações adicionais da requisição.
   * @returns Uma Promise com a resposta da requisição.
   */
  delete<T = unknown>(path: string, config: ApiRequestConfig = {}) {
    return apiInstance.delete<T>(path, config)
  },
  /**
   * Retorna os headers padrão para requisições à API idependente da versão.
   * @returns Um objeto contendo os headers.
   */
  headers(): Record<string, string> {
    const sessionStore = useSessionStore()
    const headers: Record<string, string> = {}
    if (sessionStore.token) {
      headers.Authorization = 'Bearer ' + sessionStore.token
    }
    if (sessionStore.currentEmpresa?.uuid_emp_id) {
      headers.empresalogada = sessionStore.currentEmpresa.uuid_emp_id
    }
    return headers
  },

  /**
   * Retorna a URL base da API independente da versão.
   * @returns A URL base da API.
   */
  getApUrl(): string {
    return URL
  },
  /**
   * Retorna a URL base da aplicação independente da versão.
   * @returns A URL base da aplicação.
   */
  getAplicationUrl(): string {
    return URLAplication
  },

  /**
   * Retorna o token de autenticação.
   * @returns O token de autenticação.
   */
  getTokenAut(): string {
    const sessionStore = useSessionStore()
    return 'Bearer ' + sessionStore.token
  },
}

export default Api
export const API_URL = URL
